import { createTheme } from "@mui/material/styles";
import tokens from "./tokens";

const { color, font, radius } = tokens;

/**
 * Built once at module scope. The previous theme was created inside App's render
 * body, so every render produced a new theme object and re-styled the whole tree.
 */
const theme = createTheme({
  palette: {
    primary: { main: color.indigo, dark: color.indigoHover, contrastText: "#FFFFFF" },
    success: { main: color.success },
    warning: { main: color.warning },
    error: { main: color.danger },
    background: { default: color.bg, paper: color.surface },
    text: {
      primary: color.text,
      secondary: color.textSecondary,
      disabled: color.textMuted,
    },
    divider: color.border,
  },

  typography: {
    fontFamily: font.sans,
    // The design's scale: dense, small, weight-driven rather than size-driven.
    h1: { fontSize: 22, fontWeight: 600, color: color.text },
    h2: { fontSize: 18, fontWeight: 600, color: color.text },
    h3: { fontSize: 16, fontWeight: 600, color: color.text },
    h4: { fontSize: 15, fontWeight: 600, color: color.text },
    body1: { fontSize: 13, fontWeight: 400 },
    body2: { fontSize: 12.5, fontWeight: 400 },
    caption: { fontSize: 11, fontWeight: 400, color: color.textMuted },
    button: { fontSize: 12.5, fontWeight: 600, textTransform: "none" },
  },

  shape: { borderRadius: radius.button },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: color.bg,
          color: color.text,
          fontFamily: font.sans,
          WebkitFontSmoothing: "antialiased",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: radius.button, textTransform: "none", fontWeight: 600 },
        contained: {
          backgroundColor: color.indigo,
          "&:hover": { backgroundColor: color.indigoHover },
        },
        outlined: {
          borderColor: color.borderInput,
          color: color.text,
          fontWeight: 500,
          "&:hover": { borderColor: color.borderInput, backgroundColor: color.rowTint },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: color.borderInput, "&.Mui-checked": { color: color.indigo } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.control,
          backgroundColor: color.surface,
          fontSize: 13.5,
          "& fieldset": { borderColor: color.borderInput },
          "&:hover fieldset": { borderColor: color.borderInput },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: color.dark,
          fontSize: 11.5,
          fontWeight: 400,
          borderRadius: radius.button,
          padding: "6px 9px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderColor: color.borderRow } },
    },
  },
});

export { tokens };
export default theme;
